import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, CheckCircle, AlertCircle, Download, Loader2, Building2, ExternalLink, Info, Landmark, Upload, FileText, Clock, Zap } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import axios from 'axios';
import { createPaymentIntent, confirmPayment, downloadReceipt } from '../services/paymentService';
import { CertificateYear } from '../services/otpAuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

// Tipo metodo di pagamento
type PaymentMethod = 'CARTA_CREDITO' | 'BONIFICO' | null;
type BankTransferType = 'ISTANTANEO' | 'NORMALE' | null;

// Inizializza Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface SedePrincipaleInfo {
  businessName: string;
  codice: string;
  partitaIVA: string;
  email: string;
}

interface CertifiedNetworkPaymentProps {
  certificateHistory: CertificateYear[];
  onPaymentSuccess: () => void;
  currentCertificateStatus: 'ATTIVO' | 'NON ATTIVO' | 'SCADUTO' | 'IN ATTESA' | null;
}

interface BankDetails {
  iban: string;
  bankName: string;
  accountHolder: string;
  bic: string;
}

interface PaymentSettings {
  amount: number;
  years: number[];
  yearsFormatted: string;
  description?: string;
  isSedeSecondaria?: boolean;
  sedePrincipaleHaPagato?: boolean;
  sedePrincipale?: SedePrincipaleInfo | null;
  secondarySedeEnabled?: boolean;
  // Bonifico bancario
  bankTransferEnabled?: boolean;
  bankDetails?: BankDetails;
}

/**
 * Form di pagamento Stripe
 */
const PaymentForm: React.FC<{
  settings: PaymentSettings;
  onSuccess: () => void;
  onCancel: () => void
}> = ({
  settings,
  onSuccess,
  onCancel
}) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  // Rileva dark mode per stili Stripe
  const isDarkMode = document.documentElement.classList.contains('dark');

  // Stili Stripe Elements che cambiano in base al tema
  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      color: isDarkMode ? '#e5e7eb' : '#424770', // gray-200 in dark, gray in light
      '::placeholder': {
        color: isDarkMode ? '#9ca3af' : '#aab7c4', // gray-400 in dark
      },
    },
    invalid: {
      color: '#ef4444', // red-500
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // 1. Crea Payment Intent (non serve più passare year, viene da settings backend)
      const { clientSecret, paymentIntentId } = await createPaymentIntent();

      // 2. Conferma il pagamento con Stripe
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        throw new Error('Card element non trovato');
      }

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent?.status === 'succeeded') {
        // 3. Conferma il pagamento nel backend
        const result = await confirmPayment(paymentIntentId);
        
        toast.success(
          `Pagamento confermato! Ricevuta: ${result.receipt.receiptNumber}`,
          { duration: 5000 }
        );
        
        onSuccess();
      }
    } catch (error: any) {
      console.error('Errore pagamento:', error);
      toast.error(error.message || 'Errore durante il pagamento');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-100 mb-2">
          <strong>Importo:</strong> €{settings.amount.toFixed(2)}
          {settings.isSedeSecondaria && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">(Quota Sede Secondaria)</span>
          )}
        </p>
        <p className="text-sm text-blue-900 dark:text-blue-100">
          <strong>{settings.years.length > 1 ? 'Anni Tesseramento:' : 'Anno Tesseramento:'}</strong> {settings.yearsFormatted}
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Dati Carta di Credito
        </label>

        {/* Numero Carta */}
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Numero Carta
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
            <CardNumberElement options={{ style: stripeElementStyle }} />
          </div>
        </div>

        {/* Scadenza e CVC */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Scadenza (MM/AA)
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
              <CardExpiryElement options={{ style: stripeElementStyle }} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              CVC
            </label>
            <div className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700">
              <CardCvcElement options={{ style: stripeElementStyle }} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Elaborazione...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Paga €{settings.amount.toFixed(2)}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Annulla
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        🔒 Pagamento sicuro tramite Stripe. I tuoi dati non vengono memorizzati.
      </p>
    </form>
  );
};

/**
 * Form per pagamento con bonifico bancario
 */
const BankTransferForm: React.FC<{
  settings: PaymentSettings;
  onSuccess: () => void;
  onCancel: () => void
}> = ({ settings, onSuccess, onCancel }) => {
  const toast = useToast();
  const [bankTransferType, setBankTransferType] = useState<BankTransferType>(null);
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Solo file PDF sono accettati');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('Il file non può superare 10MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bankTransferType) {
      toast.error('Seleziona il tipo di bonifico');
      return;
    }

    if (!file) {
      toast.error('Carica la distinta del bonifico');
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('associate_token');
      const formData = new FormData();
      formData.append('bankTransferReceipt', file);
      formData.append('bankTransferType', bankTransferType);

      await axios.post(
        `${API_BASE_URL}/payment/request-bank-transfer`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(
        bankTransferType === 'ISTANTANEO'
          ? 'Richiesta inviata! Riceverai conferma a breve.'
          : 'Richiesta inviata! La sede verificherà il bonifico.',
        { duration: 5000 }
      );

      onSuccess();
    } catch (error: any) {
      console.error('Errore richiesta bonifico:', error);
      toast.error(error.response?.data?.message || 'Errore durante la richiesta');
    } finally {
      setProcessing(false);
    }
  };

  const bankDetails = settings.bankDetails;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Info importo e anni */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-100 mb-2">
          <strong>Importo:</strong> €{settings.amount.toFixed(2)}
        </p>
        <p className="text-sm text-blue-900 dark:text-blue-100">
          <strong>{settings.years.length > 1 ? 'Anni Tesseramento:' : 'Anno Tesseramento:'}</strong> {settings.yearsFormatted}
        </p>
      </div>

      {/* Coordinate bancarie */}
      {bankDetails && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Landmark className="w-4 h-4" />
            Coordinate Bancarie
          </h4>
          <div className="space-y-2 text-sm">
            <p className="text-gray-700 dark:text-gray-300">
              <strong>IBAN:</strong> <span className="font-mono select-all">{bankDetails.iban}</span>
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <strong>Banca:</strong> {bankDetails.bankName}
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <strong>Intestatario:</strong> {bankDetails.accountHolder}
            </p>
            {bankDetails.bic && (
              <p className="text-gray-700 dark:text-gray-300">
                <strong>BIC/SWIFT:</strong> {bankDetails.bic}
              </p>
            )}
          </div>
          <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/30 rounded border border-amber-200 dark:border-amber-700">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              <strong>Causale:</strong> Quota RC {settings.yearsFormatted} - [TUA RAGIONE SOCIALE] - [TUA P.IVA]
            </p>
          </div>
        </div>
      )}

      {/* Selezione tipo bonifico */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tipo di Bonifico
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setBankTransferType('ISTANTANEO')}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              bankTransferType === 'ISTANTANEO'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Zap className={`w-4 h-4 ${bankTransferType === 'ISTANTANEO' ? 'text-green-600' : 'text-gray-400'}`} />
              <span className="font-semibold text-gray-900 dark:text-white text-sm">Istantaneo</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Accredito immediato. Notifica sede in 5 minuti.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setBankTransferType('NORMALE')}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              bankTransferType === 'NORMALE'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Clock className={`w-4 h-4 ${bankTransferType === 'NORMALE' ? 'text-green-600' : 'text-gray-400'}`} />
              <span className="font-semibold text-gray-900 dark:text-white text-sm">Normale</span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              1-2 giorni lavorativi. Notifica sede il giorno dopo.
            </p>
          </button>
        </div>
      </div>

      {/* Upload distinta */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Distinta Bonifico (PDF)
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            file
              ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
              <FileText className="w-5 h-5" />
              <span className="text-sm font-medium">{file.name}</span>
            </div>
          ) : (
            <div className="text-gray-500 dark:text-gray-400">
              <Upload className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Clicca per caricare la distinta del bonifico</p>
              <p className="text-xs mt-1">Solo file PDF, max 10MB</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottoni */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={processing || !bankTransferType || !file}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Invio in corso...
            </>
          ) : (
            <>
              <Landmark className="w-5 h-5" />
              Conferma Bonifico
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Annulla
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        ℹ️ La ricevuta verrà emessa dopo la conferma della ricezione del bonifico.
      </p>
    </form>
  );
};

/**
 * Componente principale per il pagamento della quota
 */
const CertifiedNetworkPayment: React.FC<CertifiedNetworkPaymentProps> = ({
  certificateHistory,
  onPaymentSuccess,
  currentCertificateStatus
}) => {
  const toast = useToast();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(null);
  const [settings, setSettings] = useState<PaymentSettings | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [hasPendingBankTransfer, setHasPendingBankTransfer] = useState(false);

  // Carica impostazioni di pagamento
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('associate_token');
        const response = await axios.get<{ success: boolean; data: PaymentSettings }>(
          `${API_BASE_URL}/payment/settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSettings(response.data.data);

        // Verifica se c'è un bonifico in attesa
        // Controllo: paymentMethod === 'BONIFICO' E paymentStatus === 'IN ATTESA'
        // OPPURE: bankTransferUploadedAt è valorizzato E paymentStatus === 'IN ATTESA' (fallback per record esistenti)
        const pendingTransfer = certificateHistory.find(
          cert => (cert.paymentMethod === 'BONIFICO' || cert.bankTransferUploadedAt) && cert.paymentStatus === 'IN ATTESA'
        );
        setHasPendingBankTransfer(!!pendingTransfer);
      } catch (error) {
        console.error('Errore caricamento impostazioni pagamento:', error);
        toast.error('Errore caricamento impostazioni pagamento');
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [certificateHistory]);

  // Verifica se ha già pagato per TUTTI gli anni configurati
  const hasPaidAllYears = settings?.years.every(year => {
    const yearCert = certificateHistory.find(cert => cert.year === year);
    return yearCert?.paymentStatus === 'PAGATO';
  }) || false;

  // Trova ricevuta più recente tra gli anni configurati
  const latestReceipt = settings?.years
    .map(year => certificateHistory.find(cert => cert.year === year))
    .filter(cert => cert?.paymentStatus === 'PAGATO')
    .sort((a, b) => (b?.year || 0) - (a?.year || 0))[0];

  const handleDownloadReceipt = async (receiptId: string) => {
    try {
      await downloadReceipt(receiptId);
      toast.success('Download ricevuta avviato');
    } catch (error) {
      console.error('Errore download ricevuta:', error);
      toast.error('Errore durante il download della ricevuta');
    }
  };

  const handlePaymentSuccess = () => {
    setSelectedPaymentMethod(null);
    onPaymentSuccess();
  };

  if (loadingSettings) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Caricamento...</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-900 dark:text-red-100">
            Impossibile caricare le impostazioni di pagamento. Riprova più tardi.
          </p>
        </div>
      </div>
    );
  }

  // Form pagamento carta di credito
  if (selectedPaymentMethod === 'CARTA_CREDITO') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Pagamento con Carta - {settings.yearsFormatted}
          </h3>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentForm
            settings={settings}
            onSuccess={handlePaymentSuccess}
            onCancel={() => setSelectedPaymentMethod(null)}
          />
        </Elements>
      </div>
    );
  }

  // Form pagamento bonifico
  if (selectedPaymentMethod === 'BONIFICO') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-green-600 dark:text-green-400" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Pagamento con Bonifico - {settings.yearsFormatted}
          </h3>
        </div>

        <BankTransferForm
          settings={settings}
          onSuccess={handlePaymentSuccess}
          onCancel={() => setSelectedPaymentMethod(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mostra sezione pagamento SOLO se currentCertificateStatus === 'IN ATTESA' */}
      {currentCertificateStatus === 'IN ATTESA' && (
        <>
          {hasPaidAllYears && latestReceipt ? (
            // Mostra ricevuta se ha già pagato tutti gli anni
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                    Quota {settings.years.length > 1 ? 'Pluriennale' : 'Annuale'} Pagata
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mb-1">
                    <strong>{settings.years.length > 1 ? 'Anni coperti:' : 'Anno coperto:'}</strong> {settings.yearsFormatted}
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300 mb-2">
                    Ricevuta: {latestReceipt.receiptNumber} • Data: {new Date(latestReceipt.paymentDate!).toLocaleDateString('it-IT')}
                  </p>
                  {latestReceipt.receiptId && (
                    <button
                      onClick={() => handleDownloadReceipt(latestReceipt.receiptId!)}
                      className="inline-flex items-center gap-2 text-xs font-medium text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Scarica Ricevuta PDF
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : settings.isSedeSecondaria && !settings.secondarySedeEnabled ? (
            // Sede secondaria con toggle DISATTIVATO: quota coperta dalla principale
            // Mostra info: quando la principale paga, questa sede sarà attivata automaticamente
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2">
                    ℹ️ Quota coperta dalla Sede Principale
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-200 mb-3">
                    La quota di questa sede secondaria sarà <strong>automaticamente attivata</strong> quando la sede principale effettuerà il pagamento.
                    Non è richiesto alcun pagamento separato per questa sede.
                  </p>

                  {settings.sedePrincipale && (
                    <div className="bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-600 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Per effettuare il pagamento, accedi con la sede principale:
                      </p>
                      <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                        <p><strong>Studio:</strong> {settings.sedePrincipale.businessName}</p>
                        <p><strong>Codice:</strong> {settings.sedePrincipale.codice}</p>
                        <p><strong>P.IVA:</strong> {settings.sedePrincipale.partitaIVA}</p>
                        <p><strong>Email:</strong> {settings.sedePrincipale.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          localStorage.removeItem('associate_token');
                          window.location.href = `/accesso-associati?piva=${settings.sedePrincipale?.partitaIVA}`;
                        }}
                        className="mt-3 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-semibold text-xs transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Accedi con Sede Principale
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : settings.isSedeSecondaria && settings.secondarySedeEnabled && !settings.sedePrincipaleHaPagato && settings.sedePrincipale ? (
            // Sede secondaria con toggle ATTIVATO: deve prima pagare la principale
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-6 h-6 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-orange-900 dark:text-orange-100 mb-2">
                    ⚠️ Prima devi pagare la Sede Principale
                  </p>
                  <p className="text-xs text-orange-800 dark:text-orange-200 mb-3">
                    Per poter pagare la quota di questa sede secondaria (€{settings.amount.toFixed(2)}), è necessario che la sede principale abbia già effettuato il pagamento.
                  </p>

                  <div className="bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-600 rounded-lg p-3 mb-3">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Per effettuare il pagamento, accedi con la sede principale:
                    </p>
                    <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                      <p><strong>Studio:</strong> {settings.sedePrincipale.businessName}</p>
                      <p><strong>Codice:</strong> {settings.sedePrincipale.codice}</p>
                      <p><strong>P.IVA:</strong> {settings.sedePrincipale.partitaIVA}</p>
                      <p><strong>Email:</strong> {settings.sedePrincipale.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        localStorage.removeItem('associate_token');
                        window.location.href = `/accesso-associati?piva=${settings.sedePrincipale?.partitaIVA}`;
                      }}
                      className="mt-3 inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg font-semibold text-xs transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Accedi con Sede Principale
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : hasPendingBankTransfer ? (
            // Bonifico in attesa di conferma
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Bonifico in Attesa di Conferma
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                    Abbiamo ricevuto la tua richiesta di pagamento con bonifico bancario.
                    La sede sta verificando la ricezione del bonifico.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    Riceverai una email con la ricevuta non appena il pagamento sarà confermato.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Mostra scelta metodo di pagamento
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                    Quota {settings.years.length > 1 ? 'Pluriennale' : 'Annuale'} {settings.yearsFormatted} Non Pagata
                    {settings.isSedeSecondaria && (
                      <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">(Sede Secondaria)</span>
                    )}
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Per mantenere attivo il tuo certificato, è necessario pagare la quota di €{settings.amount.toFixed(2)}
                    {settings.isSedeSecondaria && ' (quota ridotta per sede secondaria)'}
                    {settings.years.length > 1 && ` (valida per ${settings.yearsFormatted})`}.
                  </p>
                </div>
              </div>

              {/* Scelta metodo di pagamento */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Scegli il metodo di pagamento:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Carta di credito */}
                  <button
                    onClick={() => setSelectedPaymentMethod('CARTA_CREDITO')}
                    className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg text-left hover:border-green-400 dark:hover:border-green-500 transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                        <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="font-semibold text-gray-900 dark:text-white">Carta di Credito</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Pagamento immediato con carta. Ricevuta istantanea.
                    </p>
                  </button>

                  {/* Bonifico bancario */}
                  {settings.bankTransferEnabled && (
                    <button
                      onClick={() => setSelectedPaymentMethod('BONIFICO')}
                      className="p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg text-left hover:border-green-400 dark:hover:border-green-500 transition-all group"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                          <Landmark className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white">Bonifico bancario</span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Bonifico istantaneo o normale. Ricevuta dopo conferma della SN.
                      </p>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Storico pagamenti */}
      {certificateHistory.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Storico Pagamenti
          </h4>
          <div className="space-y-2">
            {certificateHistory.map((cert) => (
              <div
                key={cert.year}
                className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Anno {cert.year}
                    </span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className={`text-xs font-medium ${
                      cert.paymentStatus === 'PAGATO'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-orange-600 dark:text-orange-400'
                    }`}>
                      {cert.paymentStatus}
                    </span>
                  </div>
                </div>
                {cert.paymentStatus === 'PAGATO' && cert.receiptId && (
                  // Se è sede secondaria con toggle disattivato, mostra info invece di download
                  settings?.isSedeSecondaria && !settings?.secondarySedeEnabled ? (
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" title="Ricevuta disponibile dalla sede principale">
                      <Info className="w-3 h-3" />
                      Sede principale
                    </span>
                  ) : (
                    <button
                      onClick={() => handleDownloadReceipt(cert.receiptId!)}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      PDF
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertifiedNetworkPayment;

